import {RouterBridge, type RouterBridgeInstance} from "@/infrastructure/navigation";
import {SearchResultsCache, SessionStorageInstance} from "@/infrastructure/storage";
import {proxy, ref} from "valtio";

import {PointsMap, PointsMapInstance} from "@/features/rental/interactive-map";
import {PropertyFilters, type PropertyFiltersInstance} from "@/features/rental/property-filters";

import {Rental, SearchFilters, SearchResultPage} from "@/entities/rental";
import {fetchPageUnits, fetchUnitLocations, searchUnits} from "@/entities/rental/api/actions";
import type {MapPoint} from "@/entities/rental/api/types/search.types";

import {INavigable} from "@/shared/contracts";
import {BaseValtioModel} from "@/shared/models/base-valtio-model";
import {Pagination, type PaginationInstance} from "@/shared/models/pagination";
import {QueryState, type QueryStateInstance} from "@/shared/services";
import {AnalyticsService, type AnalyticsServiceInstance} from "@/shared/services/analytics/analytics.service";
import {isClient} from "@/shared/utils";
import {routes} from "@/shared/utils/routing";

import {ViewMode} from "@/app/(search)/search/parts/view-toggle";

interface SearchPageConfig extends SearchResultPage {
    initialQueryString: string;
}

// Types for query responses
type SearchQueryResult = SearchResultPage;
type SearchQueryParams = [filters?: SearchFilters];
type PaginationQueryResult = {places: Array<Rental>; points: Array<MapPoint>};

// Cache interfaces
interface PaginationCache extends Record<string, unknown> {
    pagesCache: Array<Array<string>>;
    totalUnits: number;
}

interface SearchCache extends PaginationCache {
    places: Array<Rental>;
    points: Array<MapPoint>;
    page: number;
    allUnitIds: Array<string>;
    nameLocation: string; // Location display name
}

// Helper to deep clone data (avoid Valtio proxy issues)
function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data));
}

class SearchPageClass extends BaseValtioModel implements INavigable {
    viewMode: ViewMode = "list";
    nameLocation: string = "";

    private lastQueryRequest: string = "";
    filters: PropertyFiltersInstance;
    pagination: PaginationInstance;
    map: PointsMapInstance;
    places: Array<Rental> = [];
    readonly routerBridge: RouterBridgeInstance;

    readonly searchResultsCache: SessionStorageInstance;
    // Query states for server actions
    readonly searchQuery: QueryStateInstance<SearchQueryResult, SearchQueryParams>;
    readonly paginationQuery: QueryStateInstance<PaginationQueryResult>;

    // Pagination cache from Vacasa API
    private pagesCache: Array<Array<string>> = [];
    private totalUnitsCache: number = 0;
    // Unit IDs cache - used for fetching map points
    private allUnitIds: Array<string> = [];

    // Points loading state
    private _isPointsLoading: boolean = false;

    private readonly analytics: AnalyticsServiceInstance;

    constructor(config: SearchPageConfig) {
        super();
        // Initialize cache instance first
        this.searchResultsCache = SearchResultsCache();
        this.analytics = AnalyticsService();

        // Check if bfcache restore (browser URL != SSR props) and try to restore from cache
        let actualConfig = config;
        if (isClient() && config.initialQueryString !== window.location.search.slice(1)) {
            const browserQueryString = this.normalizeQuery(window.location.search.slice(1));
            const cached = this.searchResultsCache.get<SearchCache>(browserQueryString);
            if (cached && cached.pagesCache.length > 0) {
                // Override config with cached data
                actualConfig = {
                    ...config,
                    nameLocation: cached.nameLocation, // Restore location name
                    places: cached.places,
                    points: cached.points,
                    pages: cached.pagesCache,
                    pagination: {
                        ...config.pagination,
                        total: cached.totalUnits,
                        page: cached.page,
                    },
                    initialQueryString: browserQueryString,
                };
            }
        }

        const PAGE_SIZE = actualConfig.pagination?.page || 0;
        this.nameLocation = actualConfig.nameLocation || "";
        this.filters = PropertyFilters({filter: actualConfig.filter});
        this.pagination = Pagination({
            current: PAGE_SIZE,
            pageSize: actualConfig.pagination?.pageSize || 24,
            total: actualConfig.pagination?.total || 0,
            pageSizeOptions: [12, 24, 48],
            showSizeChanger: true,
            showQuickJumper: false,
            showTotal: true,
        });
        this.places = actualConfig.places || [];

        // Parse URL params early to check for map bounds
        const params = new URLSearchParams(actualConfig.initialQueryString);
        const hasUrlBounds = params.has("neLat") && params.has("swLat") && params.has("centerLat");

        // If URL has bounds and no places found, use default geography to avoid [0,0] from API error
        const safeGeography = this.places.length === 0 && !hasUrlBounds ? actualConfig.geography : actualConfig.geography;
        const points = PAGE_SIZE > 1 ? [] : actualConfig.points || [];
        this.map = PointsMap({points: points, geography: safeGeography});

        // Sync dates to map for rental details popup pricing
        this.syncDatesToMap();

        // Cache pages for client-side pagination (from SSR)
        this.pagesCache = actualConfig.pages || [];
        this.totalUnitsCache = actualConfig.pagination?.total || 0;

        // Extract all unitIds from pages for map optimization
        const allUnitIds: Array<string> = [];
        if (actualConfig.pages && actualConfig.pages.length > 0) {
            actualConfig.pages.forEach((pageIds) => {
                allUnitIds.push(...pageIds);
            });
        }
        this.allUnitIds = allUnitIds;

        // Initialize filters from URL params if provided (normalized for consistent encoding)
        this.lastQueryRequest = this.normalizeQuery(actualConfig.initialQueryString);

        this.filters.setFromQueryParams(params);
        this.map.setFromQueryParams(params);

        this.routerBridge = ref(RouterBridge()) as RouterBridgeInstance;

        // Initialize query states
        this.searchQuery = QueryState<SearchQueryResult, SearchQueryParams>(async (signal, filters) => {
            const searchFilters = routes.search.resolve(filters ?? this.buildSearchFilters());
            const result = await searchUnits(searchFilters);

            if (!signal.aborted) {
                this.onSearchSuccess(result);
            }
            return result;
        });

        this.paginationQuery = QueryState<PaginationQueryResult>(async () => {
            const pageIndex = this.pagination.current - 1;
            const {startDate: checkIn, endDate: checkOut} = this.filters.dateFlexibility.dateRange.getDateStrings();

            const result = await fetchPageUnits({
                pageNumber: pageIndex,
                totalUnits: this.totalUnitsCache,
                nights: this.filters.dateFlexibility.committedNights ?? undefined,
                unitIds: this.pagesCache[pageIndex],
                checkIn,
                checkOut,
            });
            this.onPaginationSuccess(result);
            return result;
        });
    }

    // Loading state getter for places list
    get isLoading(): boolean {
        return this.searchQuery.isPending || this.paginationQuery.isPending;
    }

    // Loading state for map (search or points loading, but not pagination)
    get isMapLoading(): boolean {
        return this.searchQuery.isPending || this._isPointsLoading || !this.map.state.isReady;
    }

    onViewChange(view: ViewMode): void {
        this.viewMode = view;
        if (view === "map") {
            this.map.reInvalidateSize();
        }
    }

    private buildQueryString(): string {
        const params: Array<string> = [];

        const searchInBounds = this.map.getQueryParam();

        if (searchInBounds) {
            params.push(searchInBounds);
        }
        // Add filters query params
        const filtersQuery = this.filters.getQueryParam();
        if (filtersQuery) {
            params.push(filtersQuery);
        }

        // Add pagination query params
        const paginationQuery = this.pagination.getQueryParam();
        if (paginationQuery) {
            params.push(paginationQuery);
        }

        return params.join("&");
    }

    /**
     * Build SearchFilters from current state
     */
    private buildSearchFilters(): SearchFilters {
        const queryString = this.buildQueryString();
        const params = new URLSearchParams(queryString);
        return routes.search.parse(params);
    }

    private normalizeQuery(query: string): string {
        return new URLSearchParams(query).toString();
    }

    /**
     * Update URL with current query string (shallow navigation)
     */
    private updateUrl(query: string): void {
        const normalized = this.normalizeQuery(query);
        if (this.lastQueryRequest === normalized) return;
        this.lastQueryRequest = normalized;

        // No need to pass state - we use sessionStorage instead
        this.routerBridge.updateSearchParams(query);
    }

    /**
     * Handle URL changes (browser back/forward within search page)
     * Restores pagination cache from sessionStorage
     */
    onUrlChange(_pathname: string, searchParams: URLSearchParams): void {
        const queryString = this.normalizeQuery(searchParams.toString());
        // Skip if this is the same query we already processed
        if (this.lastQueryRequest === queryString) {
            return;
        }

        this.lastQueryRequest = queryString;
        this.syncFromParams(searchParams);

        // Try to restore from sessionStorage
        const cached = this.searchResultsCache.get<SearchCache>(queryString);

        if (cached && cached.pagesCache.length > 0) {
            // Restore nameLocation from cache
            this.nameLocation = cached.nameLocation || "";

            // Restore pagination cache
            this.pagesCache = cached.pagesCache;
            this.totalUnitsCache = cached.totalUnits;
            this.pagination.setTotal(cached.totalUnits, true);

            // Restore allUnitIds
            this.allUnitIds = cached.allUnitIds || [];

            // Restore map points
            this.map.sync(cached.points || []);

            void this.paginationQuery.execute();
        } else {
            // If no cache, fetch fresh data
            this.pagesCache = [];
            void this.searchQuery.execute(routes.search.parse(searchParams));
        }
    }

    /**
     * Sync all components from URL params
     */
    private syncFromParams(params: URLSearchParams): void {
        this.filters.setFromQueryParams(params);
        this.map.setFromQueryParams(params);
        this.pagination.setFromQueryParams(params);
        this.syncDatesToMap();
    }

    /**
     * Sync search params from filters to map for rental details popup pricing and URLs
     */
    private syncDatesToMap(): void {
        const {startDate, endDate} = this.filters.dateFlexibility.dateRange.getDateStrings();
        const flexible = this.filters.dateFlexibility.committedIsFlexible || undefined;
        const nights = this.filters.dateFlexibility.committedNights || undefined;

        this.map.setDates({startDate, endDate, flexible, nights});
        this.map.setLinkParams(this.paramsSinglePage);
    }

    /**
     * Execute search via QueryState
     */
    private executeSearch(): void {
        const queryString = this.buildQueryString();
        const filters = routes.search.parse(new URLSearchParams(queryString));
        this.updateUrl(queryString);
        void this.searchQuery.execute(filters);
    }

    /**
     * Execute pagination via QueryState
     */
    private executePagination(pageIndex: number): void {
        if (this.pagesCache.length === 0 || pageIndex >= this.pagesCache.length) {
            this.executeSearch();
            return;
        }

        this.updateUrl(this.buildQueryString());
        void this.paginationQuery.execute();
    }

    /**
     * Track search analytics event
     */
    private trackSearch(): void {
        try {
            const filters = this.buildSearchFilters();
            const isMapSearch = !!(filters.neLat && filters.swLat);
            const total = this.pagination.total;

            const filterCount =
                [filters.minBeds, filters.maxBeds, filters.minBaths, filters.maxBaths, filters.minRate, filters.maxRate].filter((v) => v != null)
                    .length +
                (filters.housingTypes?.length ?? 0) +
                (filters.amenities?.length ?? 0);

            this.analytics.trackSearchResults({
                search_location: this.nameLocation,
                location_slug: filters.location ?? null,
                search_type: isMapSearch ? "map" : "location",
                search_url: this.routerBridge.getUrl(),
                check_in: filters.startDate ? String(filters.startDate) : null,
                check_out: filters.endDate ? String(filters.endDate) : null,
                adults: filters.adults ?? 0,
                children: filters.children ?? 0,
                pets: filters.pets ?? 0,
                has_dates: !!(filters.startDate && filters.endDate),
                filter_count: filterCount,
                total_results: total,
                has_results: total > 0,
            });
        } catch (err) {
            console.warn("[SearchPage] trackSearch failed:", err);
        }
    }

    /**
     * Handle search query success
     */
    private onSearchSuccess(result: SearchQueryResult): void {
        this.places = result.places;
        this.pagesCache = result.pages || [];
        this.totalUnitsCache = result.pagination.total;
        const currentFilters = routes.search.resolve(this.buildSearchFilters());
        this.nameLocation = routes.search.isDefault(currentFilters) ? "Casago" : result.nameLocation || "";
        this.pagination.setTotal(result.pagination.total, true);
        this.filters.advancedFilters.sync(result.filter);

        // Extract all unitIds for map points optimization
        const allUnitIds: Array<string> = [];
        if (result.pages && result.pages.length > 0) {
            result.pages.forEach((pageIds) => {
                allUnitIds.push(...pageIds);
            });
        }

        this.allUnitIds = allUnitIds;

        // Check if we need to fetch points separately
        // This happens when:
        // 1. We have multiple pages (points only for first page in response)
        // 2. We have more units than points (some units missing points)
        const receivedPoints = result.points || [];
        const hasMultiplePages = result.pages && result.pages.length > 1;
        const hasMissingPoints = allUnitIds.length > receivedPoints.length;

        if (hasMultiplePages || hasMissingPoints) {
            void this.loadPoints();
        } else {
            this.map.sync(receivedPoints);
        }

        // If no results and no bounds from URL, update geography from API response
        if (result.places.length === 0 && !this.map.hasBounds) {
            this.map.updateGeography(result.geography);
        }

        // Save to sessionStorage for back navigation
        this.saveToCache();

        this.trackSearch();
    }

    /**
     * Handle pagination query success
     * Note: We DON'T update map points here - they remain from initial search
     */
    private onPaginationSuccess(result: PaginationQueryResult): void {
        this.places = result.places;
        // Don't update map points during pagination - they should persist

        // Save to sessionStorage for back navigation
        this.saveToCache();
    }

    /**
     * Check if points need to be loaded and load them if necessary
     * Used after restoring from cache or history state
     */
    private checkAndLoadPointsIfNeeded(): void {
        if (this.allUnitIds.length === 0) return;

        const currentPoints = this.map.state.points;
        const hasNoPoints = currentPoints.length === 0;
        const hasMultiplePages = this.pagesCache.length > 1;
        const hasMissingPoints = this.allUnitIds.length > currentPoints.length;

        if (hasNoPoints || hasMultiplePages || hasMissingPoints) {
            void this.loadPoints();
        }
    }

    /**
     * Load map points for all units
     * Uses simple HTTP client instead of QueryState for lighter approach
     */
    private async loadPoints(): Promise<void> {
        if (this.allUnitIds.length === 0) return;

        this._isPointsLoading = true;

        try {
            const points = await fetchUnitLocations(this.allUnitIds);
            this.map.sync(points || []);

            // Save to sessionStorage for back navigation
            this.saveToCache();
        } catch (error) {
            console.error("Failed to load map points:", error);
            // Keep existing points on error, don't clear the map
        } finally {
            this._isPointsLoading = false;
        }
    }

    /**
     * Save current state to sessionStorage
     */
    private saveToCache(): void {
        const queryString = this.normalizeQuery(this.buildQueryString());
        const cache: SearchCache = {
            places: cloneData(this.places),
            points: cloneData(this.map.state.points),
            pagesCache: cloneData(this.pagesCache),
            totalUnits: this.totalUnitsCache,
            page: this.pagination.current,
            allUnitIds: cloneData(this.allUnitIds),
            nameLocation: this.nameLocation, // Save location name
        };
        this.searchResultsCache.set(queryString, cache);
    }

    init(): void {
        // Check if we need to load points (if not already loaded in constructor)
        if (this.allUnitIds.length > 0) {
            this.checkAndLoadPointsIfNeeded();
        }

        // Track initial SSR search result on client hydration
        this.trackSearch();

        this.addSubscribe(
            this.filters.locationSearch.events.on("select", () => {
                this.map.clearBounds();
            }),
        );

        // When filters change - fetch via API (resets pagination cache)
        this.addSubscribe(
            this.filters.events.on(["apply", "reset"], () => {
                this.pagination.setPage(1, true);
                this.pagesCache = [];
                this.syncDatesToMap(); // Update dates for popup pricing
                this.executeSearch();
            }),
        );

        // When pagination changes - use cached pages if available
        this.addSubscribe(
            this.pagination.events.on("change", () => {
                const pageIndex = this.pagination.current - 1;
                this.executePagination(pageIndex);
            }),
        );

        // When map bounds change - fetch via API
        this.addSubscribe(
            this.map.events.on("searchInBounds", () => {
                this.pagination.setPage(1, true);
                this.pagesCache = [];
                this.filters.clearLocationForBoundsSearch();
                this.executeSearch();
            }),
        );
    }

    get paramsSinglePage(): string {
        const isFlexible = this.filters.dateFlexibility.committedIsFlexible;
        const dateParams = this.filters.dateFlexibility.dateRange.getQueryParam("checkIn", "checkOut");

        // Add flexible mode params if enabled
        const flexibleParams = isFlexible ? `flexible=true&nights=${this.filters.dateFlexibility.committedNights || ""}` : "";

        const guestParams = this.filters.guests.getQueryParam();

        return [dateParams, flexibleParams, guestParams].filter(Boolean).join("&");
    }

    get placesWithQuery(): Array<Rental> {
        const param = this.paramsSinglePage;
        return this.places.map((rental) => ({
            ...rental,
            slug: `${rental.slug}?${param}`,
        }));
    }

    openCalendar(): void {
        this.filters.dateFlexibility.open();
    }

    clearAdvanceFilter(): void {
        this.filters.clearAdvance();
    }

    override dispose(): void {
        super.dispose();
        this.routerBridge.dispose();
    }
}

export type SearchPageInstance = SearchPageClass;

export function SearchPage(config: SearchPageConfig): SearchPageInstance {
    return proxy(new SearchPageClass(config));
}
